import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Aurora from '../components/Aurora';
import GlitchText from '../components/GlitchText';
import BlurText from '../components/BlurText';
import axios from 'axios';
import './DriverDashboard.css';

const DriverDashboard = () => {
  const { user, logout } = useAuth();
  const [isOnline, setIsOnline] = useState(false);
  const [availableRides, setAvailableRides] = useState([]);
  const [myRides, setMyRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    todayRides: 0,
    completedRides: 0,
    earnings: 0
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    loadDriverData();
    setupWebSocket();
  }, []);

  const loadDriverData = async () => {
    setLoading(true);
    try {
      // Load available rides
      const ridesResponse = await axios.get(`${API_BASE_URL}/rides`);
      const allRides = ridesResponse.data.rides || [];
      
      // Filter rides
      const pending = allRides.filter(r => r.status === 'PENDING');
      const myRidesData = allRides.filter(r => r.driverId === user?.id);
      
      setAvailableRides(pending);
      setMyRides(myRidesData);

      // Calculate stats
      const today = new Date().toDateString();
      const todayRides = myRidesData.filter(r => 
        new Date(r.createdAt).toDateString() === today
      ).length;
      
      const completed = myRidesData.filter(r => r.status === 'COMPLETED').length;
      
      setStats({
        todayRides,
        completedRides: completed,
        earnings: completed * 15 // Mock earnings
      });

    } catch (error) {
      console.error('Error loading driver data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupWebSocket = () => {
    // WebSocket connection for real-time ride notifications
    const ws = new WebSocket('ws://localhost:8080');
    
    ws.onopen = () => {
      // Register as driver
      ws.send(JSON.stringify({
        type: 'driver_connect',
        driverId: user?.id
      }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_ride') {
        // Add new ride to available rides
        setAvailableRides(prev => [data.ride, ...prev]);
        
        // Show notification
        if (Notification.permission === 'granted') {
          new Notification('Nová jazda!', {
            body: `Z ${data.ride.pickupAddress} do ${data.ride.destinationAddress}`,
            icon: '/favicon.ico'
          });
        }
      }
    };

    return () => ws.close();
  };

  const toggleOnlineStatus = async () => {
    try {
      const newStatus = !isOnline;
      await axios.put(`${API_BASE_URL}/driver-status`, {
        isActive: newStatus
      });
      setIsOnline(newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const acceptRide = async (rideId) => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/accept-ride`, { rideId });
      
      // Remove from available rides
      setAvailableRides(prev => prev.filter(r => r.id !== rideId));
      
      // Reload data to get updated ride in myRides
      loadDriverData();
    } catch (error) {
      console.error('Error accepting ride:', error);
      alert('Chyba pri prijímaní jazdy: ' + (error.response?.data?.error || 'Neznáma chyba'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('sk-SK');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return '#ffd43b';
      case 'ACCEPTED': return '#51cf66';
      case 'IN_PROGRESS': return '#339af0';
      case 'COMPLETED': return '#69db7c';
      case 'CANCELLED': return '#ff6b6b';
      default: return '#868e96';
    }
  };

  // Request notification permission
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return (
    <div className="driver-dashboard">
      <Aurora />
      
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-content">
            <div className="header-info">
              <GlitchText text="Vodič Panel" className="dashboard-title" />
              <BlurText 
                text={`Vitajte, ${user?.name} | Taxi: ${user?.taxiNumber || 'N/A'}`}
                className="dashboard-subtitle"
              />
            </div>
            <div className="header-actions">
              <button 
                onClick={toggleOnlineStatus}
                className={`status-toggle ${isOnline ? 'online' : 'offline'}`}
              >
                {isOnline ? '🟢 Online' : '🔴 Offline'}
              </button>
              <button onClick={logout} className="logout-button">
                🚪 Odhlásiť sa
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🚗</div>
            <div className="stat-info">
              <div className="stat-value">{stats.todayRides}</div>
              <div className="stat-label">Dnešné jazdy</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <div className="stat-value">{stats.completedRides}</div>
              <div className="stat-label">Dokončené jazdy</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-value">{stats.earnings}€</div>
              <div className="stat-label">Výnosy</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-info">
              <div className="stat-value">{isOnline ? 'Aktívny' : 'Neaktívny'}</div>
              <div className="stat-label">Status</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-content">
          {/* Available Rides */}
          <div className="rides-section">
            <div className="section-header">
              <h3>🚨 Dostupné jazdy</h3>
              <span className="rides-count">{availableRides.length} jázd</span>
            </div>
            
            {!isOnline && (
              <div className="offline-notice">
                <div className="notice-icon">⚠️</div>
                <div className="notice-text">
                  <strong>Ste offline</strong>
                  <p>Zapnite online status pre prijímanie jázd</p>
                </div>
              </div>
            )}

            <div className="rides-list">
              {availableRides.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">😴</div>
                  <div className="empty-text">Momentálne žiadne dostupné jazdy</div>
                </div>
              ) : (
                availableRides.map(ride => (
                  <div key={ride.id} className="ride-card available">
                    <div className="ride-info">
                      <div className="ride-route">
                        <span className="pickup">📍 {ride.pickupAddress}</span>
                        <span className="arrow">→</span>
                        <span className="destination">🎯 {ride.destinationAddress}</span>
                      </div>
                      <div className="ride-details">
                        <span>📞 {ride.customerPhone}</span>
                        <span>⏰ {formatDate(ride.createdAt)}</span>
                        {ride.estimatedFare && <span>💰 ~{ride.estimatedFare}€</span>}
                      </div>
                    </div>
                    <button 
                      className="accept-button"
                      onClick={() => acceptRide(ride.id)}
                      disabled={loading || !isOnline}
                    >
                      {loading ? '⏳' : '✅'} Prijať
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* My Rides */}
          <div className="rides-section">
            <div className="section-header">
              <h3>📋 Moje jazdy</h3>
              <span className="rides-count">{myRides.length} jázd</span>
            </div>

            <div className="rides-list">
              {myRides.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <div className="empty-text">Zatiaľ žiadne prijaté jazdy</div>
                </div>
              ) : (
                myRides.map(ride => (
                  <div key={ride.id} className="ride-card my-ride">
                    <div className="ride-info">
                      <div className="ride-route">
                        <span className="pickup">📍 {ride.pickupAddress}</span>
                        <span className="arrow">→</span>
                        <span className="destination">🎯 {ride.destinationAddress}</span>
                      </div>
                      <div className="ride-details">
                        <span>📞 {ride.customerPhone}</span>
                        <span>⏰ {formatDate(ride.createdAt)}</span>
                        {ride.acceptedAt && <span>✅ Prijaté: {formatDate(ride.acceptedAt)}</span>}
                      </div>
                    </div>
                    <div 
                      className="ride-status"
                      style={{ color: getStatusColor(ride.status) }}
                    >
                      {ride.status}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDashboard; 